import React, { useEffect, useState } from "react";
import { Loader2, FileUp, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const inputCls = "w-full rounded-lg bg-ink-900 border border-white/12 px-3.5 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none text-sm";

function Field({ label, children }) {
  return <label className="block"><span className="label block mb-1.5">{label}</span>{children}</label>;
}

function PdfUpload({ name, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/programme/pdf", fd); onDone(data.pdf_name); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <p className="text-sm text-ice/80" data-testid="programme-pdf-name">{name ? <>Current PDF: <span className="text-white">{name}</span></> : "No PDF uploaded yet"}</p>
      <label className="inline-flex items-center gap-2 mt-2 rounded-full glass px-4 py-2 text-sm text-ice hover:text-cyan cursor-pointer">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} {name ? "Replace PDF" : "Upload PDF"}
        <input data-testid="programme-pdf-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {err && <p className="text-red-300 text-xs mt-2" data-testid="programme-pdf-error">{err}</p>}
    </div>
  );
}

export function ProgrammeManager() {
  const [p, setP] = useState(null);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/admin/programme").then((r) => setP(r.data)).catch(() => setP({}));
    api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);
  if (!p) return <Loader2 className="animate-spin text-cyan" />;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    if (p.published && !(Number(p.price) > 0)) return setMsg("Please set a price above £0 before showing it on the site.");
    if (p.published && !p.pdf_path) return setMsg("Please upload the programme PDF before showing it on the site.");
    setBusy(true); setMsg("");
    try {
      const { data } = await api.put("/admin/programme", { title: p.title, description: p.description, price: Number(p.price) || 0,
        addon_price: Number(p.addon_price) || 0, location_id: p.location_id || null, published: p.published });
      setP(data); setMsg("saved");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-programme">
      <p className="text-ice/70 text-sm">Sell a training programme PDF. It shows on the chosen location's page, and as a discounted add-on when someone buys a bundle for that location. Buyers get an email with a download link they can use any time.</p>
      <div className="rounded-xl border border-white/8 bg-ink-800 p-5 space-y-4">
        <Field label="Programme name"><input data-testid="programme-title" className={inputCls} value={p.title || ""} onChange={set("title")} /></Field>
        <Field label="Description"><textarea data-testid="programme-description" rows={3} className={inputCls} value={p.description || ""} onChange={set("description")} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Price on its own (£)"><input data-testid="programme-price" type="number" min="0" className={inputCls} value={p.price ?? ""} onChange={set("price")} /></Field>
          <Field label="Add-on price with a bundle (£)"><input data-testid="programme-addon-price" type="number" min="0" className={inputCls} value={p.addon_price ?? ""} onChange={set("addon_price")} /></Field>
        </div>
        <Field label="Show on location">
          <select data-testid="programme-location" className={inputCls} value={p.location_id || ""} onChange={set("location_id")}>
            <option value="">Choose a location…</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <PdfUpload name={p.pdf_name} onDone={(name) => setP({ ...p, pdf_name: name, pdf_path: "uploaded" })} />
        <label className="flex items-center gap-2 text-sm text-ice/80">
          <input data-testid="programme-published" type="checkbox" checked={!!p.published} onChange={set("published")} className="accent-cyan h-4 w-4" /> Show on the website
        </label>
        <div className="flex items-center gap-4">
          <button data-testid="programme-save" onClick={save} disabled={busy} className="book-cta disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : "Save programme"}</button>
          {msg === "saved" ? <span className="text-cyan text-sm flex items-center gap-1" data-testid="programme-saved"><Check size={15} /> Saved</span> : msg && <span className="text-red-300 text-sm" data-testid="programme-error">{msg}</span>}
        </div>
        <p className="text-mist text-xs" data-testid="programme-sold">{p.sold || 0} sold so far</p>
      </div>
    </div>
  );
}
